<template>
    <div :class="$vuetify.breakpoint.mobile ? '' : 'mb-9'">
        <!-- ---------- Desktop: top bar ---------- -->
        <header v-if="!$vuetify.breakpoint.mobile" class="top-bar">
            <div class="top-bar__inner">
                <router-link :to="{ name: 'News' }" class="top-bar__logo">
                    <img :src="require('@/assets/main_logo.png')" alt="BIG">
                </router-link>

                <nav class="top-bar__nav">
                    <router-link
                        v-for="item in mainItems"
                        :key="item.link"
                        :to="{ name: item.link }"
                        class="top-bar__link"
                        :class="{ 'top-bar__link--active': isActive(item.link) }"
                    >
                        {{ item.name }}
                    </router-link>
                    <v-menu offset-y bottom left transition="slide-y-transition" content-class="top-bar__menu">
                        <template v-slot:activator="{ on, attrs }">
                            <button
                                type="button"
                                class="top-bar__link"
                                :class="{ 'top-bar__link--active': isOrganizationActive }"
                                v-bind="attrs"
                                v-on="on"
                            >
                                Διοργάνωση
                                <v-icon small>mdi-chevron-down</v-icon>
                            </button>
                        </template>
                        <v-list dense class="top-bar__menu-list">
                            <v-list-item
                                v-for="subItem in organizationItems"
                                :key="subItem.link"
                                :to="{ name: subItem.link }"
                                :class="{ 'menu-item--active': $route.name === subItem.link }"
                            >
                                <v-list-item-icon class="mr-3">
                                    <v-icon small :color="$route.name === subItem.link ? 'orange darken-2' : 'grey darken-1'">{{ subItem.icon }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="menu-item__title">{{ subItem.name }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </nav>

                <!-- Me: my player with the badge of my team; the menu leads to the player, the team and the requests -->
                <v-menu v-if="myTeam" offset-y bottom left transition="slide-y-transition" content-class="top-bar__menu">
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="my-team-chip" v-bind="attrs" v-on="on">
                            <span v-if="myPlayer" class="my-team-chip__avatar">
                                <span class="my-team-chip__photo">
                                    <img v-if="myPlayer.image" :src="`${assetsUrl}/${myPlayer.image}`" alt="">
                                    <v-icon v-else small color="orange darken-2">mdi-account</v-icon>
                                </span>
                                <span class="my-team-chip__badge">
                                    <img v-if="myTeam.logo" :src="`${assetsUrl}/${myTeam.logo}`" alt="">
                                    <v-icon v-else x-small color="orange">mdi-account-group</v-icon>
                                </span>
                            </span>
                            <span v-else class="my-team-chip__logo">
                                <img v-if="myTeam.logo" :src="`${assetsUrl}/${myTeam.logo}`" alt="">
                                <v-icon v-else small color="orange">mdi-account-group</v-icon>
                            </span>
                            <span class="my-team-chip__text">
                                <span class="my-team-chip__name">{{ myPlayer ? shortPlayerName(myPlayer) : (myTeam.name || 'Η ομάδα μου') }}</span>
                                <span v-if="myPlayer" class="my-team-chip__sub">{{ myTeam.name }}</span>
                            </span>
                            <v-icon small>mdi-chevron-down</v-icon>
                        </button>
                    </template>
                    <v-list dense class="top-bar__menu-list">
                        <div v-if="myPlayer" class="menu-greeting">Καλώς ήρθες, {{ getPlayerGreetingName(myPlayer.name) }}!</div>
                        <v-list-item :to="{ name: 'Team', params: { id: myTeam.id } }">
                            <v-list-item-icon class="mr-3"><v-icon small color="orange darken-2">mdi-shield-star-outline</v-icon></v-list-item-icon>
                            <v-list-item-title class="menu-item__title">Η ομάδα μου</v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="myPlayer" :to="{ name: 'Player', params: { id: myPlayer.id } }">
                            <v-list-item-icon class="mr-3"><v-icon small color="orange darken-2">mdi-account-outline</v-icon></v-list-item-icon>
                            <v-list-item-title class="menu-item__title">Ο παίκτης μου</v-list-item-title>
                        </v-list-item>
                        <v-list-item :to="{ name: 'MatchChanges' }">
                            <v-list-item-icon class="mr-3"><v-icon small color="orange darken-2">mdi-calendar-sync-outline</v-icon></v-list-item-icon>
                            <v-list-item-title class="menu-item__title">Τα αιτήματά μου</v-list-item-title>
                        </v-list-item>
                        <v-list-item :to="{ name: 'Profile' }">
                            <v-list-item-icon class="mr-3"><v-icon small color="grey darken-1">mdi-cog-outline</v-icon></v-list-item-icon>
                            <v-list-item-title class="menu-item__title">Ρυθμίσεις</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <router-link v-else :to="{ name: 'Profile' }" class="my-team-chip my-team-chip--empty">
                    <v-icon small color="orange darken-2">mdi-star-plus-outline</v-icon>
                    <span class="my-team-chip__name">Διάλεξε ομάδα</span>
                </router-link>
            </div>
        </header>

        <!-- ---------- Mobile: bottom tab bar (app style) ---------- -->
        <nav v-else class="tab-bar safe-bottom-bar">
            <router-link
                v-for="item in tabItems.slice(0, 2)"
                :key="item.link"
                :to="{ name: item.link }"
                class="tab-bar__item"
                :class="{ 'tab-bar__item--active': isActive(item.link) }"
            >
                <span class="tab-bar__icon"><v-icon>{{ isActive(item.link) ? item.activeIcon : item.icon }}</v-icon></span>
                <span class="tab-bar__label">{{ item.short || item.name }}</span>
            </router-link>

            <!-- Menu: the BIG logo in the middle, raised above the bar -->
            <button
                type="button"
                class="tab-bar__item tab-bar__menu"
                :class="{ 'tab-bar__item--active': drawer || isMenuRouteActive, 'tab-bar__menu--open': drawer }"
                aria-label="Μενού"
                @click="drawer = !drawer"
            >
                <span class="tab-bar__menu-badge">
                    <img :src="require('@/assets/main_logo.png')" alt="">
                </span>
                <span class="tab-bar__label">Μενού</span>
            </button>

            <router-link
                v-for="item in tabItems.slice(2)"
                :key="item.link"
                :to="{ name: item.link }"
                class="tab-bar__item"
                :class="{ 'tab-bar__item--active': isActive(item.link) }"
            >
                <span class="tab-bar__icon"><v-icon>{{ isActive(item.link) ? item.activeIcon : item.icon }}</v-icon></span>
                <span class="tab-bar__label">{{ item.short || item.name }}</span>
            </router-link>
        </nav>

        <!-- ---------- Mobile: menu sheet ---------- -->
        <v-navigation-drawer
            v-if="$vuetify.breakpoint.mobile"
            v-model="drawer"
            app
            temporary
            right
            disable-resize-watcher
            width="86vw"
            color="#f4f4f6"
            class="menu-drawer"
            style="z-index: 101;"
        >
            <div class="menu-drawer__header safe-top-banner">
                <img :src="require('@/assets/main_logo.png')" alt="BIG" class="menu-drawer__logo">
                <div class="menu-drawer__brand">
                    <div class="menu-drawer__brand-name">BIG Basketball</div>
                    <div v-if="myPlayer" class="menu-drawer__greeting">Καλώς ήρθες, {{ getPlayerGreetingName(myPlayer.name) }}!</div>
                </div>
                <v-btn icon small class="menu-drawer__close" @click="drawer = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>

            <!-- My team card -->
            <router-link
                :to="myTeam ? { name: 'Team', params: { id: myTeam.id } } : { name: 'Profile' }"
                class="menu-team"
                @click.native="drawer = false"
            >
                <span class="menu-team__logo">
                    <img v-if="myTeam && myTeam.logo" :src="`${assetsUrl}/${myTeam.logo}`" alt="">
                    <v-icon v-else color="orange">{{ myTeam ? 'mdi-account-group' : 'mdi-star-plus-outline' }}</v-icon>
                </span>
                <span class="menu-team__text">
                    <span class="menu-team__label">{{ myTeam ? 'Η ομάδα μου' : 'Δεν έχεις διαλέξει ομάδα' }}</span>
                    <span class="menu-team__name">{{ myTeam ? (myTeam.name || 'Δες την ομάδα') : 'Διάλεξε ομάδα' }}</span>
                </span>
                <v-icon color="grey">mdi-chevron-right</v-icon>
            </router-link>

            <div class="menu-group">
                <router-link
                    v-for="item in drawerItems"
                    :key="item.link"
                    :to="{ name: item.link }"
                    class="menu-row"
                    :class="{ 'menu-row--active': isActive(item.link) }"
                    @click.native="drawer = false"
                >
                    <span class="menu-row__icon"><v-icon small color="white">{{ item.icon }}</v-icon></span>
                    <span class="menu-row__name">{{ item.name }}</span>
                    <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
                </router-link>
            </div>

            <div class="menu-group-title">Διοργάνωση</div>
            <div class="menu-group">
                <router-link
                    v-for="subItem in organizationItems"
                    :key="subItem.link"
                    :to="{ name: subItem.link }"
                    class="menu-row"
                    :class="{ 'menu-row--active': $route.name === subItem.link }"
                    @click.native="drawer = false"
                >
                    <span class="menu-row__icon menu-row__icon--dark"><v-icon small color="white">{{ subItem.icon }}</v-icon></span>
                    <span class="menu-row__name">{{ subItem.name }}</span>
                    <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
                </router-link>
            </div>

            <div class="menu-social">
                <button type="button" aria-label="Facebook" @click="openSocial('facebook')"><v-icon class="custom-orange-text">mdi-facebook</v-icon></button>
                <button type="button" aria-label="Instagram" @click="openSocial('instagram')"><v-icon class="custom-orange-text">mdi-instagram</v-icon></button>
                <button type="button" aria-label="YouTube" @click="openSocial('youtube')"><v-icon class="custom-orange-text">mdi-youtube</v-icon></button>
                <button type="button" aria-label="TikTok" @click="openSocial('tiktok')">
                    <span class="tiktok-icon" :style="tiktokMask" />
                </button>
            </div>

            <!-- Download badges only in the mobile browser, not inside the app itself -->
            <div v-if="!isNativeApp" class="menu-stores">
                <img :src="require('@/assets/google_play.png')" alt="Google Play" @click="openSocial('google_play')">
                <img :src="require('@/assets/app_store.png')" alt="App Store" @click="openSocial('app_store')">
            </div>
        </v-navigation-drawer>
    </div>
</template>

<script>
    import { Capacitor } from '@capacitor/core'

    const SOCIAL_LINKS = {
        facebook: 'https://www.facebook.com/backinthegamegr',
        instagram: 'https://www.instagram.com/backinthegame_2012/',
        youtube: 'https://www.youtube.com/@BackIntheGameBasketball',
        tiktok: 'https://www.tiktok.com/@backinthegame2012',
        google_play: 'https://play.google.com/store/apps/details?id=com.vue.big.app',
        app_store: 'https://apps.apple.com/us/app/b-i-g/id6748522198'
    }

    // Inline, so the relative asset url resolves against the page and not the built css/ folder
    const TIKTOK_URL = `url(${require('@/assets/tiktok.svg')})`
    const TIKTOK_MASK = { maskImage: TIKTOK_URL, webkitMaskImage: TIKTOK_URL }

    // Pages that belong to another section, for the active state of the menus
    const SECTION_OF = {
        Article: 'News',
        Team: 'Teams',
        Player: 'Teams',
        Match: 'Schedule'
    }

    export default {
        name: 'NavBar',
        data() {
            return {
                drawer: false,
                assetsUrl: 'https://storage.googleapis.com',
                isNativeApp: Capacitor.isNativePlatform(),
                tiktokMask: TIKTOK_MASK,
                mainItems: [
                    { name: 'Νέα', link: 'News', icon: 'mdi-newspaper-variant-outline', activeIcon: 'mdi-newspaper-variant' },
                    { name: 'Βαθμολογία', link: 'Standings', icon: 'mdi-podium', activeIcon: 'mdi-podium' },
                    { name: 'Πρόγραμμα', link: 'Schedule', icon: 'mdi-calendar-month-outline', activeIcon: 'mdi-calendar-month' },
                    { name: 'Ομάδες', link: 'Teams', icon: 'mdi-shield-outline', activeIcon: 'mdi-shield' },
                    { name: 'Στατιστικά', link: 'Stats', icon: 'mdi-chart-box-outline', activeIcon: 'mdi-chart-box' },
                    { name: 'Γήπεδα', link: 'Courts', icon: 'mdi-basketball-hoop-outline', activeIcon: 'mdi-basketball-hoop' }
                ],
                organizationItems: [
                    { name: 'Εγγραφή', link: 'Register', icon: 'mdi-account-plus-outline' },
                    { name: 'Φιλοσοφία', link: 'Philosophy', icon: 'mdi-basketball' },
                    { name: 'Ποινές', link: 'Penalties', icon: 'mdi-whistle-outline' },
                    { name: 'Free Agents', link: 'FreeAgents', icon: 'mdi-account-search-outline' },
                    { name: 'Κανονισμός', link: 'Regulations', icon: 'mdi-gavel' },
                    { name: 'Επικοινωνία', link: 'ContactUs', icon: 'mdi-message-text-outline' },
                    { name: 'Πολιτική Απορρήτου', link: 'PrivacyPolicy', icon: 'mdi-shield-lock-outline' }
                ]
            }
        },
        computed: {
            // Bottom tab bar on mobile: the four sections used the most, the rest live in the menu
            tabItems() {
                return this.mainItems.slice(0, 4).map(item => (item.link === 'Standings' ? { ...item, short: 'Βαθμολ.' } : item))
            },
            drawerItems() {
                const mine = this.myTeam ? [{ name: 'Τα αιτήματά μου', link: 'MatchChanges', icon: 'mdi-calendar-sync-outline' }] : []
                return this.mainItems.slice(4).concat(mine, { name: 'Προφίλ & ρυθμίσεις', link: 'Profile', icon: 'mdi-cog-outline' })
            },
            currentSection() {
                return SECTION_OF[this.$route.name] || this.$route.name
            },
            isOrganizationActive() {
                return this.organizationItems.some(item => item.link === this.$route.name)
            },
            isMenuRouteActive() {
                return this.isOrganizationActive || this.drawerItems.some(item => item.link === this.$route.name)
            },
            myTeam() {
                const team = this.$store.getters.getTeam
                return team && team.id ? team : null
            },
            myPlayer() {
                const player = this.$store.getters.getPlayer
                return player && player.id ? player : null
            }
        },
        watch: {
            $route() {
                this.drawer = false
            }
        },
        methods: {
            // "Π. Σπεντζούρης": fits the chip of the top bar
            shortPlayerName(player) {
                const name = (player.name || '').trim()
                const surname = (player.surname || '').trim()
                return name ? `${name.charAt(0)}. ${surname}` : surname
            },
            isActive(link) {
                return this.currentSection === link
            },
            getPlayerGreetingName(name) {
                if (!name) {
                    return ''
                }

                const firstName = String(name).trim().split(/\s+/)[0]
                const normalizedName = firstName.toLocaleLowerCase('el-GR')
                const suffixRules = [
                    { suffix: 'έας', replacement: 'έα' },
                    { suffix: 'ίας', replacement: 'ία' },
                    { suffix: 'ης', replacement: 'η' },
                    { suffix: 'ας', replacement: 'α' },
                    { suffix: 'ος', replacement: 'ο' }
                ]

                const matchedRule = suffixRules.find(rule => normalizedName.endsWith(rule.suffix))

                let vocativeName = normalizedName
                if (matchedRule) {
                    vocativeName = `${normalizedName.slice(0, -matchedRule.suffix.length)}${matchedRule.replacement}`
                }

                return vocativeName.charAt(0).toLocaleUpperCase('el-GR') + vocativeName.slice(1)
            },
            openSocial(social) {
                if (SOCIAL_LINKS[social]) {
                    window.open(SOCIAL_LINKS[social], '_blank')
                }
            }
        }
    }
</script>

<style scoped>
/* ---------- Desktop top bar ---------- */
.top-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    height: 60px;
    background: #fff;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    font-family: Ubuntu, Arial, sans-serif;
}

.top-bar::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%);
}

.top-bar__inner {
    display: flex;
    align-items: center;
    gap: 20px;
    height: 100%;
    padding: 0 24px;
}

.top-bar__logo {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
}

.top-bar__logo img {
    width: 44px;
    height: 44px;
    object-fit: contain;
}

.top-bar__nav {
    display: flex;
    flex: 1 1 auto;
    align-items: stretch;
    justify-content: center;
    gap: 4px;
    height: 100%;
}

.top-bar__link {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 0 12px;
    color: #2b2b2e !important;
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.1rem;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: color 0.15s ease;
}

.top-bar__link:hover {
    color: #ef4444 !important;
}

.top-bar__link--active {
    color: #ef4444 !important;
}

.top-bar__link--active::after {
    content: '';
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: 3px;
    height: 3px;
    border-radius: 3px 3px 0 0;
    background: #0d0d0f;
}

/* ---------- Me: my player and my team (desktop) ---------- */
.my-team-chip {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
    max-width: 220px;
    padding: 4px 10px 4px 4px;
    border-radius: 22px;
    background: #f4f4f6;
    color: #2b2b2e !important;
    font-size: 0.85rem;
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.15s ease;
}

.my-team-chip:hover {
    background: #fff3ec;
}

.my-team-chip--empty {
    padding-left: 10px;
}

.my-team-chip__logo {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #fff;
    overflow: hidden;
}

.my-team-chip__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.my-team-chip__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.15;
    text-align: left;
}

.my-team-chip__name,
.my-team-chip__sub {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.my-team-chip__sub {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.72rem;
    font-weight: 600;
}

/* my player, with the badge of my team on the corner */
.my-team-chip__avatar {
    position: relative;
    flex: 0 0 auto;
    width: 34px;
    height: 34px;
}

.my-team-chip__photo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #fff;
    overflow: hidden;

    /* the orange ring, like the avatar of the mobile app bar */
    box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(255, 113, 51, 0.6);
    transition: box-shadow 0.15s ease;
}

.my-team-chip:hover .my-team-chip__photo {
    box-shadow: 0 0 0 2px #fff, 0 0 0 3px #ef4444;
}

.my-team-chip__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
}

.my-team-chip__badge {
    position: absolute;
    right: -4px;
    bottom: -3px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 0 0 2px #f4f4f6;
    overflow: hidden;
}

.my-team-chip:hover .my-team-chip__badge {
    box-shadow: 0 0 0 2px #fff3ec;
}

.my-team-chip__badge img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

/* ---------- Mobile bottom tab bar ---------- */
.tab-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    display: flex;
    align-items: stretch;
    height: 60px;

    /* solid white, same as the app header on top */
    background: #fff;
    box-shadow: 0 -1px 10px rgba(0, 0, 0, 0.08);
    font-family: Ubuntu, Arial, sans-serif;
}

.tab-bar__item {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    min-width: 0;
    height: 60px;
    color: #8a8a8e !important;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
}

.tab-bar__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 28px;
    border-radius: 14px;
    transition: background 0.2s ease;
}

.tab-bar__icon .v-icon {
    color: inherit !important;
    font-size: 22px;
}

.tab-bar__label {
    max-width: 100%;
    overflow: hidden;
    font-size: 0.68rem;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tab-bar__item--active {
    color: #ef4444 !important;
}

/* custom-orange gradient on the selected tab's icon and label */
.tab-bar__item--active .tab-bar__label,
.tab-bar__item--active .tab-bar__icon .v-icon::before {
    background-image: linear-gradient(90deg, #ef4444 0%, #ff7133 70%, orange 100%);
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.tab-bar__item--active .tab-bar__icon {
    background: rgba(255, 113, 51, 0.14);
}

/* ---------- Center menu button (BIG logo) ---------- */
.tab-bar__menu {
    position: relative;
    justify-content: flex-end;
    padding-bottom: 6px;
}

.tab-bar__menu-badge {
    position: absolute;
    top: -22px;
    left: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    border-radius: 50%;

    /* flat white like the bars: a white ring that blends into the bar and the brand-coloured ring */
    background: #fff;
    box-shadow: 0 0 0 3px #fff, 0 0 0 5px #ff7133;
    transform: translateX(-50%);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.tab-bar__menu-badge img {
    width: 36px;
    height: 36px;
    object-fit: contain;
}

.tab-bar__menu--open .tab-bar__menu-badge {
    box-shadow: 0 0 0 3px #fff, 0 0 0 5px #ef4444;
    transform: translateX(-50%) rotate(-12deg);
}

.tab-bar__menu:active .tab-bar__menu-badge {
    transform: translateX(-50%) scale(0.94);
}

/* ---------- Mobile menu sheet ---------- */
.menu-drawer {
    font-family: Ubuntu, Arial, sans-serif;
    text-align: left;
}

.menu-drawer__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 14px 10px;
    background: #fff;
}

.menu-drawer__logo {
    width: 40px;
    height: 40px;
    object-fit: contain;
}

.menu-drawer__brand {
    flex: 1 1 auto;
    min-width: 0;
}

.menu-drawer__brand-name {
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.2rem;
    font-weight: 800;
}

.menu-drawer__greeting {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.8rem;
    font-weight: 600;
}

.menu-team {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 12px 12px 0;
    padding: 12px;
    border-radius: 14px;
    background: #fff;
    border-left: 4px solid #ff7133;
    color: inherit !important;
    text-decoration: none;
}

.menu-team__logo {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #f4f4f6;
    overflow: hidden;
}

.menu-team__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.menu-team__text {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
}

.menu-team__label {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.menu-team__name {
    overflow: hidden;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.menu-group-title {
    margin: 18px 24px 6px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

/* iOS-settings style grouped list */
.menu-group {
    margin: 12px 12px 0;
    border-radius: 14px;
    background: #fff;
    overflow: hidden;
}

.menu-group-title + .menu-group {
    margin-top: 0;
}

.menu-row {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 48px;
    padding: 0 12px;
    color: #2b2b2e !important;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
}

.menu-row + .menu-row {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.menu-row:active {
    background: #f4f4f6;
}

.menu-row__icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: linear-gradient(135deg, #ef4444 0%, #ff7133 60%, orange 100%);
}

.menu-row__icon--dark {
    background: #0d0d0f;
}

.menu-row__name {
    flex: 1 1 auto;
    font-size: 0.95rem;
    font-weight: 600;
}

.menu-row--active .menu-row__name {
    color: #ef4444;
    font-weight: 800;
}

.menu-social {
    display: flex;
    justify-content: center;
    gap: 22px;
    margin: 20px 12px 0;
}

.menu-social button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: #fff;
}

/* TikTok is an svg: used as a mask over the custom-orange gradient, so it matches the font icons.
   The mask url is set inline (tiktokMask): a url() inside a custom property resolves against the
   built css/ folder, which broke the relative asset path in production */
.tiktok-icon {
    display: inline-block;
    width: 20px;
    height: 20px;
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 70%, orange 100%);
    mask-position: center;
    mask-size: contain;
    mask-repeat: no-repeat;
    /* stylelint-disable property-no-vendor-prefix -- Safari and the Android webviews of the app still need the prefix */
    -webkit-mask-position: center;
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    /* stylelint-enable property-no-vendor-prefix */
}

.menu-stores {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin: 18px 12px 24px;
}

.menu-stores img {
    width: 130px;
    height: auto;
    cursor: pointer;
}

.menu-drawer ::v-deep .v-navigation-drawer__content {
    padding-bottom: 24px;
}
</style>

<style>
/* Menus of the desktop bar (rendered outside the component) */
.top-bar__menu {
    border-radius: 12px !important;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16) !important;
}

.top-bar__menu-list {
    min-width: 220px;
    padding: 6px !important;
    font-family: Ubuntu, Arial, sans-serif;
}

.top-bar__menu-list .v-list-item {
    min-height: 38px !important;
    border-radius: 8px;
}

.top-bar__menu-list .menu-item__title {
    color: #2b2b2e;
    font-size: 0.9rem !important;
    font-weight: 600 !important;
}

.top-bar__menu-list .menu-item--active .menu-item__title {
    color: #ef4444;
    font-weight: 800 !important;
}

.top-bar__menu-list .menu-greeting {
    padding: 8px 12px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.8rem;
    font-weight: 700;
}
</style>
